import React from 'react';
import { INTENTIONALLY_ABORTED_SIGNAL } from 'Integrations/Sentry';

export default function useAbortAllButLatest() {
  const abortControllerRef = React.useRef(null);

  return {
    /**
     * The first time you call this, it gives you a new AbortSignal and a cancel function.
     * All future times you call this, it aborts the previous AbortSignal, and returns new ones.
     *
     * For example, if you want to make an HTTP request as the user is typing in a search box, but then the user types more and you want to cancel it, each time you make a request you can ask for a new signal and give the signal to fetch or to facet-rest, e.g. link.get({ signal })
     *
     * Note that this always aborts the oldest signal. If you have 1 component or hook that does 2 different requests, you may want to call this hook twice so that they can have 2 different latest AbortSignals, and get abortions independently
     * @returns {{ signal: AbortSignal, cancel: () => void }}
     */
    newSignal() {
      // Abort the last signal, if it's not already - it's no longer the latest
      if (abortControllerRef.current !== null && !abortControllerRef.current.signal.aborted) {
        abortControllerRef.current.abort(INTENTIONALLY_ABORTED_SIGNAL);
      }
      // Create a new abort controller to keep track of
      const controller = new AbortController();
      abortControllerRef.current = controller;
      return {
        signal: controller.signal,
        // Safer way to abort - it won't abort more than once, and it provides a signal that Sentry will ignore
        abort() {
          if (!controller.signal.aborted) {
            controller.abort(INTENTIONALLY_ABORTED_SIGNAL);
          }
        },
      };
    },
    /**
     * Only returns true if there's been at least one signal created and the latest signal has not been aborted
     * @returns {boolean}
     */
    inProgress() {
      return abortControllerRef.current !== null && !abortControllerRef.current.signal.aborted;
    },
    complete() {
      abortControllerRef.current = null;
    },
  };
}
